import type { VerticalConfig } from '../../interfaces/vertical-config.interface';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Aligns the term's case with the call site's reference label. Backend terms
 * are capitalized, so a lowercase reference (a label used mid-sentence, e.g.
 * "...assign tracking numbers") gets the term lowercased ("service lines"),
 * while a capitalized reference (a standalone label, e.g. "Tracking Number")
 * leaves it capitalized ("Service lines").
 */
function matchCase(value: string, referenceLabel?: string): string {
  if (!referenceLabel) return value;
  const startsLower = /^[a-z]/.test(referenceLabel);
  return startsLower ? value.toLowerCase() : value;
}

/**
 * Maps a call-site key to the agency's vertical term. The term dictionary lives
 * entirely on the backend (VerticalConfig); this only maps keys to fields and
 * adjusts casing — it never invents or derives copy. Returns undefined when the
 * field is unset so the caller falls back to its default English label.
 */
export function deriveLabel(
  key: string,
  config: VerticalConfig,
  referenceLabel?: string
): string | undefined {
  const field = FIELD_BY_KEY[key];
  const value = field ? config[field] : undefined;
  return typeof value === 'string' && value
    ? matchCase(capitalize(value), referenceLabel)
    : undefined;
}

// Each UI key reads one dedicated backend term. Synonyms (receptionist/rep) and
// plurals (agents/customers/tracking_numbers) resolve to their own backend
// fields — the frontend no longer pluralizes or composes anything itself.
const FIELD_BY_KEY: Record<string, keyof VerticalConfig> = {
  customer: 'customer_term',
  customers: 'customers_term',
  appointment: 'appointment_term',
  appointments: 'appointments_term',
  appointments_booked: 'appointments_booked_term',
  booked: 'booked_term',
  not_booked: 'not_booked_term',
  agent: 'agent_term',
  receptionist: 'agent_term',
  rep: 'agent_term',
  agents: 'agents_term',
  receptionists: 'agents_term',
  reps: 'agents_term',
  lead_center_agent: 'lead_center_agent_term',
  lead_center_agents: 'lead_center_agents_term',
  calls_by_agent: 'calls_by_agent_term',
  tracking_number: 'tracker_term',
  tracking_numbers: 'trackers_term',
};
